import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { PropsOf } from '@blaze-runtime/engine-domain';
import { DataTypeRefInfo } from '@blaze-shared/api-contracts-domain';
import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import {
  type ElementFormState,
  getBackground,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
  scaleBorderRadius,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import {
  DatasetColumn,
  getDisplayStringFromLinkValue,
  isDatasetLinkValue,
} from '@blaze-shared/schema-domain';
import { ClickableDirective, safeDetectChanges, ScrollerComponent } from '@blaze-shared/shared-ui';
import { type Nullish, useEmitTracker } from '@blaze-shared/shared-util';
import {
  FileResult,
  getItemTypeIcon,
  isFileResult,
  UploaderFacade,
  UploaderOptions,
} from '@blaze-shared/uploader';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { getFileFromUrl } from '../../utils/data/file.utils';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { FileDownloadLinkDirective } from './file-download-link.directive';
import { DEFAULT_UPLOAD_FILE_COLUMNS } from './upload-file.model';
import { UploadFileService } from './upload-file.service';
import { renameToMp4 } from './utils/rename-to-mp4';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    ClickableDirective,
    ElementLabelComponent,
    FileDownloadLinkDirective,
    MatIcon,
    NgStyle,
    ScrollerComponent,
  ],
})
export class UploadFileComponent
  extends CanvasElementBaseComponent<'uploadFile'>
  implements CanvasElementComponent<'uploadFile'>, OnChanges, OnInit
{
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly uploader = inject(UploaderFacade);
  private readonly uploadFileService = inject(UploadFileService);

  @Input() value: Nullish<string | string[] | FileResult | FileResult[]>;
  @Input() formState?: ElementFormState;

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');

  acceptedExtensions = '*.*';
  capture?: 'environment' | 'user';

  labelEnabled!: boolean;
  labelText!: string;

  buttonLabel!: string;
  buttonIcon!: string;

  sectionStyles!: Record<string, string>;
  fileListStyles!: Record<string, string>;
  dragAndDropStyles!: Record<string, string>;
  dragAndDropActionStyles!: Record<string, string>;
  uploadStyles!: Record<string, string>;
  uploadedStyles!: Record<string, string>;
  uploadedResetStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;

  selectedFiles: Nullish<FileResult[]>;
  isMultiple!: boolean;
  isReadOnly!: boolean;

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isUploadPossible(): boolean {
    return !this.isReadOnly && (this.isMultiple || !this.selectedFiles?.length);
  }

  get isDragAndDropEnabled(): boolean {
    return this.props?.content?.options?.style === 'drag-drop';
  }

  get isFileSecure(): boolean {
    return this.dataValue.column?.isSecure ?? false;
  }

  get isSortingEnabled(): boolean {
    return !this.isReadOnly && (this.selectedFiles?.length ?? 0) > 1;
  }

  private get shouldKeepSelection(): boolean {
    // If the input is bound (for example as a part of a form) - the selection should be kept
    return !!this.bindingResults?.['value'];
  }

  private get shouldSaveAsMp4(): boolean {
    const capture = this.props?.content?.options?.capture;
    return !!capture?.enabled && capture.format === 'video' && !!capture.saveAsMp4;
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this.acceptedExtensions = this.getSupportedFileTypes().join(', ');

      const { button, title } = this.props?.content || {};
      this.labelEnabled = title?.enabled ?? false;
      this.buttonLabel = button?.label || '';
      this.buttonIcon = button?.icon || '';

      this.isMultiple = this.props?.content?.options?.multiple || false;

      this.capture = this.props?.content?.options?.capture?.enabled
        ? this.props.content.options.capture.defaultCamera
        : undefined;

      this.updateStyles();
    }

    const isMultiplePrevious = (changes['props']?.previousValue as PropsOf<'uploadFile'>)?.content
      ?.options?.multiple;
    const hasMultipleSelectionChanged =
      isMultiplePrevious !== undefined && this.isMultiple !== isMultiplePrevious;

    if (hasMultipleSelectionChanged) {
      this.setSchemaToEmit();
    }

    // Update output if new value or value already present and multiple selection was toggled
    if (changes?.['value'] || (this.value && hasMultipleSelectionChanged)) {
      if (isDatasetLinkValue(this.value)) {
        this.value = getDisplayStringFromLinkValue(this.value.display);
      }
      this.updateOutput(this.value);
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.isReadOnly = features.isReadOnly || this.props?.content?.button?.readonly || false;

        safeDetectChanges(this.cdr);
      });
    this.setSchemaToEmit();
    if (!this.bindingResults?.['value']) {
      this.updateOutput(this.value);
    }
  }

  emitClick(toolbarTriggered = false): void {
    if (!this.isInEditor || toolbarTriggered) {
      if (this.isUploadPossible) {
        this.fileInputRef.nativeElement.click();
      }
    }
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const files = Array.from(fileInputElement?.files ?? []);
    fileInputElement.value = '';

    if (!files || files.length === 0) {
      return;
    }
    this.upload(files);
  }

  getFileTypeIcon(file: FileResult): string {
    return getItemTypeIcon(file.filename);
  }

  resetUpload(selectedFile: FileResult): void {
    if (this.isReadOnly) {
      return;
    }

    this.selectedFiles = this.selectedFiles?.filter((file) => file !== selectedFile);
    this.updateOutput(this.selectedFiles);
  }

  reorderFiles(event: CdkDragDrop<FileResult[]>): void {
    if (this.isReadOnly || event.previousIndex === event.currentIndex) {
      return;
    }

    const files = Array.from(this.selectedFiles ?? []);
    moveItemInArray(files, event.previousIndex, event.currentIndex);
    this.selectedFiles = files;
    this.updateOutput(this.selectedFiles);
  }

  allowDrop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  drop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.upload(event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : undefined);
  }

  private upload(preselectFiles?: File[]): void {
    if (this.isReadOnly) {
      return;
    }

    // Rename captured videos to .mp4 so downstream consumers accept them (see renameToMp4)
    if (this.shouldSaveAsMp4 && preselectFiles) {
      preselectFiles = preselectFiles.map(renameToMp4);
    }

    const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);

    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      preselectFiles,
      acceptedFileTypes: this.getSupportedFileTypes(),
      isMultipleSelection: this.isMultiple || false,
    };

    // If upload is to secure vault - pass project id and the flag
    if (this.dataValue.column?.isSecure) {
      options.projectId = projectId;
      options.isSecure = true;
    }

    const saveAsBase64 = this.props?.content?.options?.saveAs === 'base64';

    this.uploadFileService.processUpload(options, saveAsBase64).subscribe((results) => {
      if (!results) {
        return;
      }

      const selected = Array.from(this.selectedFiles || []);
      selected.push(...results);

      if (this.shouldKeepSelection) {
        this.selectedFiles = selected;
      }

      this.updateOutput(selected, true);
      this.cdr.detectChanges();
    });
  }

  private getSupportedFileTypes(): string[] {
    const useAll = this.props?.content?.fileFormats?.enabled;
    const extensions: string[] = [];

    if (useAll) {
      return ['*.*'];
    }

    const videoFormatTypes = ['.avi', '.mkv', '.mov', '.mp4', '.mpeg', '.mpg', '.webm', '.wmv'];

    const capture = this.props?.content.options.capture;
    if (capture?.enabled) {
      return capture.format === 'image'
        ? this.uploader.supportedImageTypes.toSorted((a, b) => a.localeCompare(b))
        : videoFormatTypes;
    }

    if (this.props?.content?.fileFormats?.csv) {
      extensions.push('.csv');
    }

    if (this.props?.content?.fileFormats?.docs) {
      extensions.push(
        '.doc',
        '.docx',
        '.pdf',
        '.ppt',
        '.pptx',
        '.ods',
        '.odt',
        '.txt',
        '.xls',
        '.xlsx',
      );
    }

    if (this.props?.content?.fileFormats?.image) {
      extensions.push(...this.uploader.supportedImageTypes);
    }

    if (this.props?.content?.fileFormats?.video) {
      extensions.push(...videoFormatTypes);
    }

    if (this.props?.content?.fileFormats?.audio) {
      extensions.push(
        '.aac',
        '.aiff',
        '.alac',
        '.flac',
        '.m4a',
        '.mp3',
        '.mpga',
        '.ogg',
        '.wav',
        '.wma',
      );
    }

    return extensions.sort();
  }

  private updateOutput(
    data: Nullish<string | string[] | FileResult | FileResult[]>,
    forceUpdate = false,
  ): void {
    let outputData;

    // If data present - unify it to an array of FileResult objects
    if (data) {
      outputData = Array.isArray(data) ? data : [data];
      outputData = outputData
        .map((file) => (isNonEmptyString(file) ? getFileFromUrl(file) : file))
        .filter(isFileResult)
        .map((file) => ({
          ...file,
          size: file.size ?? null,
          thumbnail: file.thumbnail ?? null,
        }));
    }

    // If non-empty string, proceed with the 'URL' flow
    if (this.shouldKeepSelection) {
      this.selectedFiles = outputData;
    }

    // If the value was initially undefined, keep it undefined (so it doesn't trigger workflows)
    const emptyValue = data === undefined ? undefined : null;

    // Normalize data for output based (array for multiple, single value otherwise)
    outputData = this.isMultiple ? outputData || [] : (outputData?.[0] ?? emptyValue);

    const isInitialData = !this.outputEmitTracker.hasPrevious();
    // shouldEmit check must be called before checking forceUpdate to ensure hashes inside that function are properly updated
    if (this.outputEmitTracker.shouldEmit(outputData) || forceUpdate) {
      this.value = outputData;
      this.output.emit({
        eventType: 'change',
        data: outputData,
        isInitialData,
        userUpdate: forceUpdate,
        schema: this.getEventSchema('change'),
      });
    }
    this.cdr.detectChanges();
  }

  private setSchemaToEmit(): void {
    if (this.isMultiple) {
      const dataTypeDef: DataTypeRefInfo = {
        columns: DEFAULT_UPLOAD_FILE_COLUMNS,
        name: 'File',
      };
      const columns: DatasetColumn[] = [
        {
          columnType: 'Object',
          dataType: 'Object',
          dataTypeDef,
          isProfilePicture: false,
          multipleValues: true,
          name: '',
        },
      ];
      const schema = this.createElementSchema('change', columns);
      this.setDefaultEventSchema('change', schema);
    } else {
      this.setDefaultEventSchema('change');
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { button, general, dragDrop, upload, uploaded, labelBlock } = style || {};
    if (
      !style ||
      !button ||
      !general ||
      !title ||
      !dragDrop ||
      !upload ||
      !uploaded ||
      !labelBlock
    ) {
      return;
    }

    const sectionStyles: Record<string, string> = {};
    const dragAndDropStyles: Record<string, string> = {
      color: dragDrop.label.color1,
      background: getBackground(dragDrop.fill),
      ...makeFontStyles(dragDrop.font),
      ...makeFontStyleStyles(dragDrop.font?.style, '700'),
      ...makeLabelHorizontalAlignStyles(dragDrop?.align || 'center'),
      ['font-weight']: this.fontsService.getFontWeight(
        dragDrop.font?.fontFamily,
        dragDrop.font?.fontWeight,
      ),
    };
    if (dragDrop.outline.selectorType !== 'none') {
      dragAndDropStyles['border-color'] = dragDrop.outline.color1;
      if (dragDrop.outlineWidth !== '0px') {
        dragAndDropStyles['border-width'] = dragDrop.outlineWidth;
      }
    }
    if (dragDrop.radius !== '0px') {
      dragAndDropStyles['border-radius'] = dragDrop.radius;
    }
    const dragAndDropActionStyles: Record<string, string> = {
      color: dragDrop.action.color1,
    };
    const uploadStyles: Record<string, string> = {
      color: upload.label.color1,
      background: getBackground(upload.fill),
      ...makeFontStyles(upload.font),
      ...makeFontStyleStyles(upload.font?.style, '700'),
      ...makeLabelHorizontalAlignStyles(upload?.align || 'center'),
      ['font-weight']: this.fontsService.getFontWeight(
        upload.font?.fontFamily,
        upload.font?.fontWeight,
      ),
    };
    const uploadedStyles: Record<string, string> = {
      ...makeFontStyles(style),
      color: uploaded.label.color1,
      background: getBackground(uploaded.fill),
    };
    const uploadedResetStyles: Record<string, string> = {};
    const fileListStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};

    if (title.position === 'top') {
      sectionStyles['flex-direction'] = 'column';
    } else {
      sectionStyles['align-items'] = 'flex-start';
      validationStyles['margin-left'] = title.width;
      validationStyles['padding-left'] = '8px';
      if (title.enabled) {
        fileListStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    if (this.props?.content?.button?.iconPosition === 'right') {
      uploadStyles['flex-direction'] = 'row-reverse';
    }
    if (upload.outline.selectorType !== 'none') {
      uploadStyles['border-color'] = upload.outline.color1;
      if (button.outlineWidth !== '0px') {
        uploadStyles['border-width'] = button.outlineWidth;
      }
    }
    if (uploaded.outline.selectorType !== 'none') {
      uploadedStyles['border-color'] = uploaded.outline.color1;
      uploadedResetStyles['border-color'] = uploaded.outline.color1;
      if (button.outlineWidth !== '0px') {
        uploadedStyles['border-width'] = button.outlineWidth;
        uploadedResetStyles['border-width'] = button.outlineWidth;
      }
    }
    if (button.radius !== '0px') {
      fileListStyles['border-radius'] = button.radius;
      uploadStyles['border-radius'] = button.radius;
      uploadedStyles['border-radius'] = button.radius;
      uploadedResetStyles['border-radius'] = scaleBorderRadius(button.radius, 0.5);
    }

    this.sectionStyles = sectionStyles;
    this.fileListStyles = fileListStyles;
    this.dragAndDropStyles = dragAndDropStyles;
    this.dragAndDropActionStyles = dragAndDropActionStyles;
    this.uploadStyles = uploadStyles;
    this.uploadedStyles = uploadedStyles;
    this.uploadedResetStyles = uploadedResetStyles;
    this.validationStyles = validationStyles;
  }
}

function isNonEmptyString(data: Nullish<string | FileResult>): data is string {
  return !!data && typeof data === 'string';
}
